import type { CustomFormApi } from 'informed';
import type { Arguments } from 'payments-lib-types';
import type { RefObject } from 'react';
import { dateFormat, removeSpaces } from '@/helpers/common';
import { countriesWithPhoneNumber } from '@/helpers/countriesWithPhoneNumber';
import { validateDate, validatePhone } from '@/helpers/validators';
import type {
  PersistentFormName,
  PersistentFormStore,
} from '@/store/commonStore/slices/persistant/usePersistentFormsSlice';
import { type IProfileInfoFormData, TEXT_FIELDS } from './config';

export const persistProfileInfoForm = (
  formApiRef: RefObject<CustomFormApi<IProfileInfoFormData>>,
  setPersistentFormData: PersistentFormStore['setPersistentFormData'],
  formName: PersistentFormName,
  excludeFields?: string[]
) => {
  const formValues = formApiRef.current?.getFormState().values;

  if (!formValues) return;

  const values = excludeFields
    ? Object.fromEntries(
        Object.entries(formValues).filter(
          ([key]) => !excludeFields.includes(key)
        )
      )
    : formValues;

  setPersistentFormData(formName, values);
};

export const formatProfileInfoFormValues = (values: IProfileInfoFormData) => {
  const formattedValues: Arguments.SavePlayerFieldsArgs['playerFieldsData'] = {
    ...values,
  };

  if (values.dateOfBirth) {
    const { day, month, year } = values.dateOfBirth;
    formattedValues.dateOfBirth = dateFormat(`${day}/${month}/${year}`);
  }

  if (values.mobilePhone) {
    const { code, number } = values.mobilePhone;
    const mobileCountry = countriesWithPhoneNumber.find(
      ({ iso2 }) => iso2 === code
    );

    if (mobileCountry) {
      formattedValues.mobilePhone = removeSpaces(
        `+${mobileCountry.dialCode}${number}`
      );
    }
  }

  return formattedValues;
};

const textFieldValidators = Object.values(TEXT_FIELDS).reduce<
  Record<string, ((value?: string) => unknown) | undefined>
>((acc, field) => {
  if (field) acc[field.name] = field.validate;
  return acc;
}, {});

const isValidDateOfBirth = (
  value: IProfileInfoFormData['dateOfBirth'],
  countryCode?: string
) => {
  const { day, month, year } = value ?? {};

  return Boolean(
    day &&
      month &&
      year &&
      !validateDate(countryCode)(`${day}/${month}/${year}`)
  );
};

const isValidMobilePhone = (value: IProfileInfoFormData['mobilePhone']) => {
  const { code, number } = value ?? {};

  return Boolean(code && number && !validatePhone(number, code));
};

const isValidTextField = (key: string, value: unknown) => {
  const validate = textFieldValidators[key];

  return !validate?.(value as string);
};

export const getValidProfileInfoValues = (
  values: IProfileInfoFormData,
  countryCode?: string
): IProfileInfoFormData => {
  const result: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(values)) {
    if (value == null) continue;

    if (key === 'dateOfBirth') {
      if (isValidDateOfBirth(value, countryCode)) result.dateOfBirth = value;
      continue;
    }

    if (key === 'mobilePhone') {
      if (isValidMobilePhone(value)) result.mobilePhone = value;
      continue;
    }

    if (isValidTextField(key, value)) result[key] = value;
  }

  return result as IProfileInfoFormData;
};
